/**
 * CUIT/CUIL: el verificador, y el que le corresponde a un número de documento.
 *
 * Vive acá, entre los módulos espejo, porque la misma regla tiene que correr en
 * los dos lados: el browser la usa al leer el DNI —para no meter en el campo
 * fiscal un CUIL mal leído— y el server la vuelve a aplicar sin confiar en el
 * cliente. Escrita dos veces se desincroniza, que es exactamente lo que ya pasó
 * con la regla de legibilidad del MRZ.
 *
 * Solo corrección matemática: un CUIT válido acá puede no existir en AFIP.
 *
 * ESPEJO byte-idéntico en jupiter-client/src/common/pages/dniUpload/cuit.ts
 */

/** Pesos del módulo 11 de AFIP sobre los 10 primeros dígitos. */
const PESOS = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];

/**
 * El verificador de un CUIT dados sus 10 primeros dígitos, o null si no hay
 * uno posible.
 *
 * El 10 no es un dígito y AFIP directamente no emite esos números: cuando el
 * cálculo da 10, el CUIT no existe con ese prefijo. Devolver null y no un 0 es
 * lo que le permite a quien llama distinguir "no válido" de "verificador cero".
 */
export function digitoVerificadorCuit(diezDigitos: string): number | null {
  const d = String(diezDigitos ?? "").replace(/\D/g, "");
  if (d.length !== 10) return null;
  const suma = PESOS.reduce((acc, peso, i) => acc + peso * Number(d[i]), 0);
  const resto = suma % 11;
  const dv = resto === 0 ? 0 : 11 - resto;
  return dv === 10 ? null : dv;
}

/** Los 11 dígitos de un CUIT escrito de cualquier forma, o null. */
function eldigitos(raw: string | null | undefined): string | null {
  const d = String(raw ?? "").replace(/\D/g, "");
  if (d.length === 11) return d;
  // Un DNI de 7 cifras escrito sin su cero a la izquierda: misma tolerancia que
  // las máscaras del DSL, que ya la tenían.
  if (d.length === 10) return d.slice(0, 2) + "0" + d.slice(2);
  return null;
}

export function esCuitValido(raw: string | null | undefined): boolean {
  const d = eldigitos(raw);
  if (!d) return false;
  const dv = digitoVerificadorCuit(d.slice(0, 10));
  return dv !== null && dv === Number(d[10]);
}

/** El número de documento que lleva un CUIT adentro (los dígitos 3 a 10). */
export function dniDeCuit(raw: string | null | undefined): string | null {
  const d = eldigitos(raw);
  return d ? d.slice(2, 10) : null;
}

/** Compara por el tramo del medio: el prefijo de una misma persona puede variar. */
export function cuitContieneDni(cuit: string | null | undefined, dni: string): boolean {
  const delCuit = dniDeCuit(cuit);
  const esperado = String(dni ?? "").replace(/\D/g, "").replace(/^0+/, "");
  if (!delCuit || !esperado) return false;
  return delCuit.replace(/^0+/, "") === esperado;
}

/**
 * El CUIT que le corresponde a un documento. Es una INFERENCIA, no un dato
 * leído: el prefijo sale del sexo y el documento no lo imprime.
 *
 * 20 para masculino y 27 para femenino. Si con ese prefijo el verificador daría
 * 10 —número que AFIP no emite— el prefijo pasa a 23, que es la regla para esos
 * casos. Si tampoco cierra, no se devuelve nada: es preferible no ofrecer un
 * CUIT a ofrecer uno inventado para un campo fiscal.
 */
export function cuitParaDni(
  dni: string,
  sexo: "male" | "female" | null | undefined
): string | null {
  const numero = String(dni ?? "").replace(/\D/g, "");
  if (numero.length === 0 || numero.length > 8) return null;
  const prefijoBase = sexo === "male" ? "20" : sexo === "female" ? "27" : null;
  if (!prefijoBase) return null;

  const medio = numero.padStart(8, "0");
  for (const prefijo of [prefijoBase, "23"]) {
    const dv = digitoVerificadorCuit(prefijo + medio);
    if (dv !== null) return `${prefijo}-${medio}-${dv}`;
  }
  return null;
}

export type VeredictoCuit =
  | "sin_cargar"
  | "coincide"
  | "es_dni"
  | "no_coincide"
  | "sin_documento";

export interface RevisionCuit {
  veredicto: VeredictoCuit;
  /** El que le corresponde al documento, o null si no se pudo inferir. */
  sugerido: string | null;
  /**
   * El prefijo del cargado no puede ser de alguien de ese sexo: 20 es de
   * varones y 27 de mujeres. Va aparte del veredicto porque no invalida el
   * CUIT —el número del medio puede estar perfecto— y porque el desacuerdo
   * puede venir de cualquiera de los tres lados: el CUIT mal tipeado, el
   * género mal cargado, o el propio documento mal leído (el sexo es el único
   * campo del MRZ que ningún dígito de control cubre).
   */
  prefijoContradiceSexo: boolean;
}

/** 20 es de varones, 27 de mujeres; 23 y 24 no dicen nada del sexo. */
function contradicePrefijo(cuit: string, sexo: "male" | "female"): boolean {
  const dos = String(cuit ?? "").replace(/\D/g, "").slice(0, 2);
  if (sexo === "male") return dos === "27";
  return dos === "20";
}

/**
 * El CUIT cargado en el formulario, mirado contra el documento.
 *
 * Coincide cuando lleva adentro el mismo número Y su verificador cierra: un
 * CUIT con el número correcto pero el verificador roto no es el de nadie.
 *
 * El prefijo NO entra en la comparación. Una misma persona puede tener 20, 23 o
 * 27 según cómo se lo emitieron, así que exigir el inferido marcaría como
 * equivocados CUITs que están perfectos.
 *
 * El campo puede venir cargado como CUIT/CUIL o como DNI —son dos tipos del
 * mismo formulario—, y un DNI correcto no es un desajuste: es la misma
 * identidad escrita de forma más pobre. Ese caso sale como `es_dni`, para poder
 * ofrecer completarlo en vez de marcarlo como error. El tipo se deduce del
 * largo y no del campo `taxIdType`: un CUIT tipeado en un campo marcado como
 * DNI sigue siendo un CUIT, y al revés.
 */
export function revisarCuitCargado(
  cargado: string | null | undefined,
  dniDelDocumento: string,
  sexo: "male" | "female" | null | undefined
): RevisionCuit {
  const numero = String(dniDelDocumento ?? "").replace(/\D/g, "");
  if (!numero) {
    return { veredicto: "sin_documento", sugerido: null, prefijoContradiceSexo: false };
  }

  const sugerido = cuitParaDni(numero, sexo);
  const digitos = String(cargado ?? "").replace(/\D/g, "");
  if (digitos.length === 0) {
    return { veredicto: "sin_cargar", sugerido, prefijoContradiceSexo: false };
  }

  // El prefijo solo dice algo cuando hay un CUIT de once dígitos y un sexo
  // contra el cual mirarlo: un campo cargado con el DNI pelado no tiene prefijo.
  const prefijoContradiceSexo =
    digitos.length === 11 && !!sexo && contradicePrefijo(digitos, sexo);

  if (digitos.length === 7 || digitos.length === 8) {
    const igual = digitos.replace(/^0+/, "") === numero.replace(/^0+/, "");
    return { veredicto: igual ? "es_dni" : "no_coincide", sugerido, prefijoContradiceSexo };
  }
  const coincide = esCuitValido(cargado) && cuitContieneDni(cargado, numero);
  return {
    veredicto: coincide ? "coincide" : "no_coincide",
    sugerido,
    prefijoContradiceSexo,
  };
}
